import React from 'react';
import { Backdrop, CircularProgress, Portal } from '@mui/material';
import { useHttpInFlight } from '@/hooks/useHttpInFlight';

/**
 * Application-level loading overlay.
 *
 * Mount once at the App root (after `<Admin>`'s providers so MUI theme
 * and React-Admin context are available). Reads the global in-flight
 * HTTP counter — when ANY `httpClient` request is pending, renders a
 * fullscreen blocking backdrop with a centered spinner; auto-hides
 * the moment the last request settles.
 *
 * Design choices:
 *  - Single source of truth lives in `httpClient` → no per-feature
 *    loading state, no manual state management in callers.
 *  - `Backdrop` is `position: fixed` and uses `theme.zIndex.modal + 1`
 *    so it sits above MUI Dialogs, RA's notification snackbar, and
 *    custom popovers — clicks are absorbed, scrolling can't fight it.
 *  - Light translucent veil keeps the form readable underneath; users
 *    see WHAT is loading instead of a black void.
 *  - 100ms render delay so very fast requests (cached responses,
 *    optimistic round-trips) don't flash the spinner — feels less
 *    busy without changing the contract.
 */
export const GlobalBackdrop: React.FC = () => {
  const inFlight = useHttpInFlight();
  const open = inFlight > 0;

  return (
    <Portal>
      <Backdrop
        open={open}
        transitionDuration={{ enter: 100, exit: 0 }}
        sx={(theme) => ({
          // Non-dimming, non-blurring: transitions between screens should
          // not fog the UI — only a centered spinner while requests fly.
          zIndex: theme.zIndex.modal + 10,
          bgcolor: 'transparent',
          backdropFilter: 'none',
          color: theme.custom.rg.accent,
        })}
      >
        <CircularProgress color="inherit" size={40} thickness={4} />
      </Backdrop>
    </Portal>
  );
};

export default GlobalBackdrop;
